import KeenSlider, {
  TDetails, TOptions, TOptionsEvents,
  TSlides,
} from 'keen-slider';
import { selectors } from '../Constants';

const arrowPrev = `
  <div class="arrow arrow--prev">
    <svg
      class="arrow__icon arrow__icon--prev"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
    >
      <path
        d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z"
      ></path>
    </svg>
  </div>
`;

const arrowNext = `
  <div class="arrow arrow--next">
    <svg
      class="arrow__icon arrow__icon--next"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
    >
      <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z"></path>
    </svg>
  </div>
`;

const defaultOptions = {
  centered: false,
  controls: true,
  arrows: false,
  dragSpeed: 1,
  duration: 500,
  friction: 0.0025,
  initial: 0,
  loop: false,
  mode: 'snap',
  resetSlide: false,
  rtl: false,
  rubberband: true,
  slidesPerView: 1,
  spacing: 0,
  vertical: false,
  lazyLoad: true,
} as TOptions;

type LRKeenSliderOptions = {
  arrows: boolean;
  lazyLoad: boolean;
} & TOptionsEvents;

type ExternalSliderTriggerEvent = CustomEvent<{
  sliderId: string;
  direction?: 'prev' | 'next',
  index?: number;
}>;

/**
 * Lively Root KeenSlider component.
 * This class will be instantiated for every element
 * with the data-keen-slider attribute.
 *
 * Two or more elements with the same data-keen-slider value will
 * be considered sibling sliders and will listen to each other
 * for certain events.
 *
 * If the element also has the attribute data-keen-slider-is-nav and that
 * value is true, the object instantiated from this class will have
 * additional properties and behaviors.
 */
export default class LRKeenSlider {
  el: HTMLElement;

  $el: JQuery<Element>;

  options: LRKeenSliderOptions;

  isNavigation = false;

  sliderID: string;

  slider: KeenSlider;

  loaded: Array<any> = [];

  imgUrls: Array<string> = [];

  mousedown: any = {
    x: 0.0,
    y: 0.0,
  }

  isSwipeGesture = false;

  isCoordinatedSlideChange = false;

  lightboxAttr?: string;

  constructor(el: HTMLElement) {
    /**
     * Wrapper element for this KeenSlider.
     */
    this.el = el;

    this.$el = $(el);

    /**
     * The slider ID is used to identify the slider and link any other
     * sliders that share the same ID.  Two or more sliders may share the same ID,
     * in which case the sliders will listen to each other for certain events.
     */
    // @ts-ignore
    this.sliderID = this.el.dataset.keenSlider;
    if (!this.el || !this.sliderID) {
      return;
    }

    /**
     * Determines whether or not this slider is acting as a navigation slider
     * for a main or other set of sliders.
     */
    // @ts-ignore
    if (this.el.dataset.keenSliderIsNav || this.el.dataset.keenSliderIsNav === 'true') {
      this.isNavigation = true;
    }

    /**
     * Parse json options from the liquid file.
     */
    let options: {
      arrows: boolean;
      lazyLoad: boolean;
    } & TOptionsEvents;
    try {
      const optionsEl = this.el.querySelector(selectors.keenSlider.options);

      if (optionsEl) {
        options = JSON.parse(this.el.querySelector(selectors.keenSlider.options).innerHTML) || {};
      }

      options = {
        ...defaultOptions,
        ...options,
      };
    } catch (err) {
      console.log('failed to parse slider options');
      return;
    }

    /**
     * Tell KeenSlider that any element within this.el that has the
     * selector data-keen-slide is a slide we want to include in
     * this slider.
     */
    options.slides = this.el.querySelectorAll(selectors.keenSlider.slide);

    /**
     * Read and set the initial slide set in the liquid template
     */
    let initialSlideIndex = 0;
    var initialSlide: number = initialSlide || 0;

    if (initialSlide !== undefined) {
      const matchedSlide = this.$el.find(`[data-config-id="${initialSlide}"`);
      initialSlideIndex = Math.max(this.$el.find(selectors.keenSlider.slide).index(matchedSlide), 0);
    }

    options.initial = initialSlideIndex;

    /**
     * Define callbacks for KeenSlider
     */
    options.created = ((instance: KeenSlider) => {
      this.created(instance);
    });

    options.mounted = ((instance: KeenSlider) => {
      this.mounted(instance);
    });

    options.beforeChange = ((instance: KeenSlider) => {
      this.beforeChange(instance);
    });

    options.afterChange = ((instance: KeenSlider) => {
      this.afterChange(instance);
    });

    options.slideChanged = ((instance: KeenSlider) => {
      this.slideChanged(instance);
    });

    options.dragEnd = ((instance: KeenSlider) => {
      this.dragEnd(instance);
    });

    /**
     * We'll use this later on so we store it in state for now.
     */
    this.options = options;

    /**
     * Instantiate the KeenSlider object with our options.
     */
    this.slider = new KeenSlider(this.el, options);

    /**
     * If options.arrows is true, append arrow elements with the
     * sliderID and -prev or -next.
     */
    if (options.arrows) {
      this.insertArrows();
    }

    if (options.lazyLoad) {
      // todo.
    }

    const thumbnails = this.$el.find(selectors.keenSlider.thumbnails);
    if (thumbnails[0]) {
      const lightboxAttr = thumbnails[0].getAttribute('data-fancybox');
      this.lightboxAttr = lightboxAttr;
    }

    this.orchestrateForFractionalPixelSizing();

    this.bindEvents();

    if (!global.__updateSliderQueue__) {
      global.__updateSliderQueue__ = [];
    } else {
      const {
        imageId,
        sizeChanged,
      } = global.__updateSliderQueue__[global.__updateSliderQueue__.length - 1] || {};

      if (imageId) {
        this.updateSliderContent(imageId);
        this.moveToSlideWithImageId(imageId, sizeChanged);
      }
    }
  }

  /** Fractional pixel breaks keen-slider's size calcuation as it uses offsetWidth internally
   * which is a rounded number whilst the actual size of the container/slides can be fractional.
   * We are rounding it to the lower bound(minus 1 pixel) to prevent it from being larger than
   * available container width
  */
  private orchestrateForFractionalPixelSizing() {
    if (this.options.vertical) {
      return;
    }

    this.el.style.width = '';
    this.el.style.width = `${this.el.offsetWidth - 1}px`;

    if (this.slider) {
      this.slider.resize();
    }
  }

  private bindEvents() {
    this.created = this.created.bind(this);
    this.mounted = this.mounted.bind(this);
    this.slideChanged = this.slideChanged.bind(this);
    this.beforeChange = this.beforeChange.bind(this);
    this.afterChange = this.afterChange.bind(this);
    this.dragEnd = this.dragEnd.bind(this);
    this.moveToSlide = this.moveToSlide.bind(this);
    this.siblingSlideChanged = this.siblingSlideChanged.bind(this);
    this.updateClasses = this.updateClasses.bind(this);
    this.mouseDown = this.mouseDown.bind(this);
    this.mouseUp = this.mouseUp.bind(this);
    this.onExternalSliderTrigger = this.onExternalSliderTrigger.bind(this);
    this.preventLightboxOnSwipe = this.preventLightboxOnSwipe.bind(this);
    this.onSlideChangedCustomEvent = this.onSlideChangedCustomEvent.bind(this);
    this.orchestrateForFractionalPixelSizing = this.orchestrateForFractionalPixelSizing.bind(this);

    /**
     * Listens to changes on main sliders with this Slider ID.
     */
    if (this.isNavigation) {
      window.addEventListener(`${this.sliderID}-main-changed`, this.onSlideChangedCustomEvent);

      window.addEventListener('updateProductSlider', (e: CustomEvent<{ imageId: string, sizeChanged?: boolean}>) => {
        const { imageId } = e.detail;

        this.updateSliderContent(imageId);
        this.updateClasses(this.slider);
      });

      // $(window).on(`${this.sliderID}-main-changed`,
      //   (event, data) => {
      //   if (data.lrSlider && data.lrSlider !== this) {
      //     this.siblingSlideChanged(data.lrSlider);
      //   }
      // });
    } else {
      /**
     * Listens to changes on navigation sliders with this Slider ID.
     */
      window.addEventListener(`${this.sliderID}-nav-changed`, this.onSlideChangedCustomEvent);

      /**
       * Listen to event fired during product variant selection to move to corresponding image
       */
      window.addEventListener('updateProductSlider', (e: CustomEvent<{ imageId: string, sizeChanged?: boolean }>) => {
        const { imageId, sizeChanged } = e.detail;
        if (imageId) {
          this.updateSliderContent(imageId);
          // Trigger move event only on the main slider
          this.moveToSlideWithImageId(imageId, sizeChanged);
        }
      });

      /**
       * Listen to event fired during product variant selection to move to corresponding image
       */
      $(window).on('productVariantSwitch', (event, data: any) => {
        this.moveToSlideWithVariant(data.variant);
      });
    }

    document.addEventListener('move-slider', this.onExternalSliderTrigger);

    window.addEventListener('resize', this.orchestrateForFractionalPixelSizing);

    /**
     * These events help us determine if a user is trying to click on
     * a slide or drag a slide.  Refer to this.mouseUp() and this.isSwipeGesture for
     * use case.
     */
    const this1 = this;
    this.el.addEventListener('mousedown', (e: MouseEvent) => {
      this1.mouseDown(e);
    });
    this.el.addEventListener('mouseup', (e: MouseEvent) => {
      this1.mouseUp(e);
    });

    $(this.el).find(selectors.keenSlider.thumbnails).on('click', this.preventLightboxOnSwipe);
  }

  /**
   * Fires once the KeenSlider is mounted.
   * Sets a timeout and adds a class to this element's overlay div
   * to fade it out once the slider is ready to be shown.
   *
   * This is most useful when the slider contains images and is
   * adjusting layout  while the images are being loaded.
   * @param instance
   */
  public mounted(instance: KeenSlider) {
    const overlay = this.el.querySelector(selectors.keenSlider.overlay);
    if (overlay) {
      overlay.classList.add('fade-out');
      setTimeout(() => {
        overlay.remove();
      }, 500);
    }
  }

  /**
   * KeenSlider - Created
   *
   * Attach click listener to the slider if it is a navigation slider.
   * Note the this.isSwipeGesture property.  This helps us differentiate between
   * a slide or swipe gesture and a click or tap.
   * @param instance
   */
  public created(instance: KeenSlider) {
    if (this.isNavigation) {
      const { slides } = instance.options();
      const self = this;

      (slides as NodeListOf<Element>).forEach(
        (slide, index, _slides) => {
          if (!slide.classList.contains('empty-slide')) {
            slide.addEventListener('click', (e) => {
              if (!self.isSwipeGesture) {
                const visibleSlides = [..._slides].filter((s) => (s as HTMLElement).style.display !== 'none');
                const slideIndex = visibleSlides.findIndex((s) => s === slide);
                self.moveToSlide(slideIndex);
              }
            });
          }
        },
      );
    }
  }

  private onSlideChangedCustomEvent(e: CustomEvent) {
    const { lrSlider } = e.detail;

    if (lrSlider && lrSlider !== this) {
      this.siblingSlideChanged(lrSlider);
    }
  }

  private onExternalSliderTrigger(e: ExternalSliderTriggerEvent) {
    const { sliderId, direction, index } = e.detail;
    if (sliderId !== this.sliderID) {
      return;
    }

    if (Number.isInteger(index)) {
      this.moveToSlide(index);
    } else if (direction === 'next') {
      this.slider.next();
    } else {
      this.slider.prev();
    }
  }

  private broadcastChangeEvent(eventName: string) {
    if (this.slider) {
      window.dispatchEvent(new CustomEvent(`${this.sliderID}-slider-events:${eventName}`, {
        detail: {
          data: this.slider.details(),
        },
      }));
    }
  }

  public beforeChange(instance: KeenSlider) {
  }

  /**
   * KeenSlider - afterChange
   *
   * Used to emit events to other sliders linked to this one through the
   * sliderID property.
   * @param instance
   */
  public afterChange(instance: KeenSlider) {
    this.isCoordinatedSlideChange = false;
    this.handleShippingBadgeVisiblity(instance.details());
    this.broadcastChangeEvent('afterChange');
  }

  /**
   * KeenSlider - dragEnd
   * @param instance
   */
  public dragEnd(instance: KeenSlider) {
  }

  /**
   * KeenSlider - sideChanged
   * @param instance
   */
  public slideChanged(instance: KeenSlider) {
    // If this has been triggered due to a sibling slider, this shouldn't trigger further events
    if (instance && !this.isCoordinatedSlideChange) {
      const eventName = this.isNavigation ? `${this.sliderID}-nav-changed` : `${this.sliderID}-main-changed`;
      window.dispatchEvent(new CustomEvent(eventName, {
        detail: {
          lrSlider: this,
        },
      }));
    }

    this.broadcastChangeEvent('slideChanged');

    this.updateClasses(instance);
  }

  /**
   * Sets mouseDown values
   * @param e
   */
  public mouseDown(e: MouseEvent) {
    this.mousedown.x = e.clientX;
    this.mousedown.y = e.clientY;
  }

  /**
   * Calculate delta in x an y between mousedown and mouseup,
   * depending on value, set this.isSwipeGesture to true or false.
   * @param e
   */
  public mouseUp(e: MouseEvent) {
    const deltaX = Math.abs(this.mousedown.x - e.clientX);
    const deltaY = Math.abs(this.mousedown.y - e.clientY);
    this.isSwipeGesture = deltaX >= 5 || deltaY >= 5;
  }

  /**
   * Called when a sibling slider has changed position.
   * @param lrSlider
   */
  public siblingSlideChanged(lrSlider: LRKeenSlider) {
    if (!lrSlider.slider) {
      return;
    }

    // @ts-ignore
    const details = lrSlider.slider.details();
    this.isCoordinatedSlideChange = true;
    this.moveToSlide(details.relativeSlide);
  }

  private async updateSliderContent(imageId: string) {
    if (!imageId) { return; }
    const options = { ...this.options };

    // Hide all slides first
    this.$el.find('[data-keen-slide]')
      .css('display', 'none')
      .find('[data-lr-fancybox]')
      .removeAttr('data-fancybox');

    // Display all detail, featured and matching pot config images
    const slidesToShow = this.$el.find(`[data-keen-slide]:not([data-config-id]),[data-config-id="${imageId}"]`);
    slidesToShow
      .css('display', 'list-item')
      .find('[data-lr-fancybox]')
      .attr('data-fancybox', this.lightboxAttr);

    if (!this.isNavigation) {
      const dotsWrapperEl = this.$el.find('.keen-slider__dots');
      const dots = [...slidesToShow].map(() => '<div class="keen-slider__dot"></div>').join('');
      dotsWrapperEl.html(dots);
    }

    options.slides = this.$el[0].querySelectorAll(`[data-keen-slide]:not([data-config-id]),[data-config-id="${imageId}"]`);
    this.slider.refresh(options);
  }

  /**
   * Move to slide based on the passed image id.
   * @param imageId (string) The config image  id to move to
   */
  private async moveToSlideWithImageId(imageId: string, sizeChanged?: boolean) {
    if (!imageId) { return; }

    const currentVariantId = await global.__getABTestVariantAsync__('1004100565');
    const configVariant = currentVariantId !== '1004241193' ? 'v1' : 'v2';

    // Reset image slider to position 0 if v1 variant or if
    // plant size has been changed
    if (configVariant === 'v2' && !sizeChanged) { return; }

    const slide = this.$el.find(`[data-config-id=${imageId}][data-config-variant="${configVariant}"]`).first();
    if (!slide.length) { return; }

    const index = this.$el.find(selectors.keenSlider.slide).not(':hidden').index(slide);

    if (index !== -1) {
      this.moveToSlide(index);
    }
  }

  /**
   * Move to slide based on the passed variant.
   * @param variant (object) The active variant object
   */
  private moveToSlideWithVariant(variant: any) {
    if (!variant || !variant.featured_image) { return; }

    const { src } = variant.featured_image;

    // @ts-ignore
    const sizedSrc = Shopify.Image.getSizedImageUrl(src, '960x');

    const imageEl = this.el.querySelector(`img[data-src="${sizedSrc}"]`);

    if (!imageEl) { return; }

    const slide = imageEl.closest('.keen-slider__slide');

    if (!slide) { return; }

    const index = this.$el.find(selectors.keenSlider.slide).index(slide);

    if (index !== -1) {
      this.moveToSlide(index);
    }
  }

  /**
   * Move slider to a specified index.
   *
   * todo: use this method to move slider depending on product options.
   * @param index
   */
  public moveToSlide(index: number) {
    if (!this.slider) {
      return;
    }

    this.slider.moveToSlide(index);
    this.updateClasses(this.slider);
  }

  private insertArrows() {
    const arrowsNode = document.createElement('div');

    arrowsNode.classList.add('keen-slider__controls');
    arrowsNode.innerHTML = `
      ${arrowPrev}
      ${arrowNext}
    `;

    this.el.appendChild(arrowsNode);
    const arrowsPrevious = this.el.getElementsByClassName('arrow--prev');
    const arrowsNext = this.el.getElementsByClassName('arrow--next');

    if (arrowsPrevious.length) {
      arrowsPrevious[0].addEventListener('click', (e) => {
        this.slider.prev();
      });
    }

    if (arrowsNext.length) {
      arrowsNext[0].addEventListener('click', () => this.slider.next());
    }

    const { size, absoluteSlide } = this.slider.details();

    // Required for first time the slider loads as the event callback fires
    // earlier than the arrows get rendered
    this.updateArrowClasses(size, absoluteSlide);
  }

  /**
   * Sets 'active' class and disabled arrows.
   * @param instance
   * @private
   */
  private updateClasses(instance: KeenSlider) {
    if (!instance) {
      return;
    }
    // Slides
    const { slides } = instance.options();
    const { absoluteSlide: activeIndex, size } = instance.details();

    // @ts-ignore
    slides.forEach((slide, index) => {
      if (index === activeIndex) {
        slide.classList.add('active');
      } else {
        slide.classList.remove('active');
      }
    });

    if (!this.isNavigation) {
      const dots = this.$el.find('.keen-slider__dots .keen-slider__dot');
      dots.removeClass('active');
      $(dots[activeIndex]).addClass('active');
    }

    this.updateArrowClasses(size, activeIndex);
  }

  private updateArrowClasses(size: number, activeIndex: number) {
    // Arrow Controls
    const arrowsPrevious = this.el.getElementsByClassName('arrow--prev');
    const arrowsNext = this.el.getElementsByClassName('arrow--next');
    if (arrowsPrevious.length) {
      activeIndex === 0 && !this.options.loop
        ? arrowsPrevious[0].classList.add('arrow--disabled')
        : arrowsPrevious[0].classList.remove('arrow--disabled');
    }
    if (arrowsNext.length) {
      (activeIndex === size - 1) && !this.options.loop
        ? arrowsNext[0].classList.add('arrow--disabled')
        : arrowsNext[0].classList.remove('arrow--disabled');
    }
  }

  private preventLightboxOnSwipe(e: JQuery.Event) {
    if (this.isSwipeGesture) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private handleShippingBadgeVisiblity({ relativeSlide, direction }: TDetails) {
    const $activeSlide = this.$el.find('.keen-slider__slide').eq(relativeSlide);
    const $previouslyActiveSlide = direction === 1 ? $activeSlide.prev() : $activeSlide.next();

    if ($previouslyActiveSlide.hasClass('featured')) {
      $previouslyActiveSlide.find('.scheduled-delivery-badge').removeClass('show');
    }

    if ($activeSlide.hasClass('featured')) {
      $activeSlide.find('.scheduled-delivery-badge').addClass('show');
    }
  }
}
